import React, {useEffect, useRef} from 'react';

import {
  arrow,
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import {useToggle} from 'react-use';

import bem from 'client/services/bem';

import AppButton from 'client/common/buttons/app-button';
import Icon from 'client/common/icon';
import {POPOVER_PORTAL_ID} from 'client/common/popovers/popover';

import {TranslationJsx} from 'client/models/language/types';

import cssModule from './dropdown.module.scss';

const b = bem('dropdown', {cssModule});

const ICON_SIZE = 16;
const ARROW_OFFSET = 1;
const ARROW_Y_OFFSET = -7;

/**
 * @type {React.FC<InferProps<typeof Dropdown.propTypes>>}
 */
const Dropdown = (props) => {
  const {
    options,
    position = 'bottom',
    commonOnClick = null,
    className = '',
    optionsClassName = null,
    trigger = null,
    triggerProps = {},
    showToggleArrow = false,
    showMenuArrow = false,
    disabled: mainDisabled,
    onOpen,
    offsetParams,
    hideIfEmpty,
    classNames,
    onTriggerClick,
    iconName = 'options',
    isHoverDropdown = false, // if we have dropdown by hovering
    getPlacement,
    stopPropagationOnToggle,
  } = props;
  const [isMenuVisible, toggleIsMenuVisible] = useToggle(false);
  const arrowRef = useRef(null);

  const {
    refs,
    floatingStyles,
    context,
    placement,
    middlewareData: {arrow: {x: arrowX, y: arrowY} = {}},
  } = useFloating({
    open: isMenuVisible,
    onOpenChange: () => {
      onOpen?.(!isMenuVisible);
      toggleIsMenuVisible();
    },
    middleware: [flip({mainAxis: true}), offset(offsetParams), arrow({element: arrowRef})],
    placement: position,
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    if (isMenuVisible && placement) {
      getPlacement?.(placement);
    }
  }, [placement, isMenuVisible, getPlacement]);

  const role = useRole(context, {role: 'menu'});
  const dismiss = useDismiss(context);

  const {getFloatingProps} = useInteractions([dismiss, role]);

  const handleToggleMenu = (e) => {
    if (stopPropagationOnToggle) {
      e.stopPropagation();
    }
    onOpen?.(!isMenuVisible);
    toggleIsMenuVisible();
  };

  const renderOption = (option) => {
    const {key, label, value, onClick, disabled, exclude, active, ...restOptionProps} = option;
    if (exclude) {
      return null;
    }
    const handleClick = (e) => {
      if (stopPropagationOnToggle) {
        e.stopPropagation();
      }
      if (onClick) {
        onClick();
      } else if (commonOnClick) {
        commonOnClick(value);
      }
      onOpen?.(false);
      toggleIsMenuVisible();
    };
    return (
      <li key={key || value || label} className={b('option-wrapper')} {...restOptionProps}>
        <button
          type="button"
          className={cn(b('option', {disabled, active}), option.className)}
          disabled={disabled}
          onClick={handleClick}
        >
          {option.label}
        </button>
      </li>
    );
  };

  const toggleArrow = showToggleArrow ? (
    <Icon
      className={b('arrow')}
      name={isMenuVisible ? 'arrow-up' : 'arrow-down'}
      width={ICON_SIZE}
      height={ICON_SIZE}
    />
  ) : null;

  const displayedOptions = options.filter((option) =>
    !option.options ? !option.exclude : !!option.options?.filter((opt) => !opt.exclude).length,
  );

  if (hideIfEmpty && !displayedOptions.length) {
    return null;
  }

  const getArrowStyles = () => {
    if (!showMenuArrow) {
      return {};
    }

    const styles = {};
    const placementValue = placement || position;
    const arrowLeft = showToggleArrow ? arrowX - ICON_SIZE + ARROW_OFFSET : arrowX;

    if (placementValue?.includes('bottom')) {
      styles.top = arrowY ?? ARROW_Y_OFFSET;
      styles.left = arrowLeft ?? 0;
    } else if (placementValue?.includes('top')) {
      styles.bottom = arrowY ?? ARROW_Y_OFFSET;
      styles.left = arrowLeft ?? 0;
    } else if (placementValue?.includes('right')) {
      styles.right = arrowY ?? 0;
      styles.top = arrowX ?? ARROW_Y_OFFSET;
    } else if (placementValue?.includes('left')) {
      styles.left = arrowY ?? 0;
      styles.top = arrowX ?? ARROW_Y_OFFSET;
    }

    return styles;
  };

  const stylesArrow = getArrowStyles();

  return (
    <div
      className={cn(
        b({'is-menu-visible': isHoverDropdown && isMenuVisible, 'is-menu-hidden': isHoverDropdown && !isMenuVisible}),
        className,
      )}
    >
      <div ref={refs.setReference} className={cn(b('trigger-container'), classNames?.triggerContainer)}>
        {trigger ? (
          <button
            className={cn(b('trigger-wrapper'), classNames?.trigger)}
            onClick={onTriggerClick || handleToggleMenu}
            disabled={mainDisabled}
          >
            {trigger}
            {toggleArrow}
          </button>
        ) : (
          <AppButton
            iconName={iconName}
            color="light-clients"
            onClick={onTriggerClick || handleToggleMenu}
            label={toggleArrow}
            {...triggerProps}
          />
        )}
      </div>
      {isMenuVisible && (
        <FloatingPortal id={POPOVER_PORTAL_ID}>
          <div className={b('menu-container')} ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
            {showMenuArrow && (
              <div
                ref={arrowRef}
                style={stylesArrow}
                className={b('menu-arrow', placement ? {[placement]: true} : {})}
              />
            )}
            <ul className={cn(b('options'), optionsClassName)}>
              {displayedOptions.map((option) =>
                option.options?.length
                  ? [
                      <li key={option.key || options.value || option.label} className={b('group-label')}>
                        {option.label}
                      </li>,
                      ...(option.options?.map(renderOption) || []),
                    ]
                  : renderOption(option),
              )}
            </ul>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

const optionPropType = {
  label: PropTypes.oneOfType([TranslationJsx, PropTypes.element]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  exclude: PropTypes.bool,
  active: PropTypes.bool,
};

Dropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      ...optionPropType,
      options: PropTypes.arrayOf(PropTypes.shape(optionPropType)),
    }),
  ).isRequired,
  hideIfEmpty: PropTypes.bool,
  commonOnClick: PropTypes.func,
  onOpen: PropTypes.func,
  position: PropTypes.oneOf(['left', 'right', 'bottom', 'top', 'top-end', 'top-start', 'bottom-end', 'bottom-start']),
  className: PropTypes.string,
  /** @deprecated Use classNames */
  optionsClassName: PropTypes.string,
  trigger: PropTypes.node,
  triggerProps: PropTypes.object,
  showToggleArrow: PropTypes.bool,
  showMenuArrow: PropTypes.bool,
  offsetParams: PropTypes.object,
  classNames: PropTypes.object,
  iconName: PropTypes.string,
  onTriggerClick: PropTypes.func,
  getPlacement: PropTypes.func,
  stopPropagationOnToggle: PropTypes.bool,

  /* @deprecated prop*/
  grouped: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default Dropdown;
